import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../providers/AuthContext';
import { showSuccess, showError, showConfirm } from '../../utils/sweetAlert';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import Loader from '../shared/Loader';

const DescriptionManager = () => {
  const { token } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/categories`);
      const data = await response.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      showError('Error al cargar las categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCategory = async (category) => {
    setSelectedCategory(category);
    setPreviewMode(false);
    try {
      const response = await fetch(`${API_URL}/products/sample-description/${category._id}`);
      const data = await response.json();
      setHtmlContent(data.description || getDefaultTemplate());
    } catch (error) {
      setHtmlContent(getDefaultTemplate());
    }
  };

  const getDefaultTemplate = () => {
    return `<!-- Intro -->
<div style="padding:14px 16px; border:1px solid #e7e7e7; border-radius:12px; background:#fff;">
  <p style="margin:0; font-size:14px; color:#333;">
    Producto diseñado para sumar estilo y personalidad a tus espacios. Ideal para regalar o para darle un detalle único a tu hogar.
  </p>
</div>

<!-- Highlights -->
<div style="margin-top:12px; padding:14px 16px; border:1px solid #e7e7e7; border-radius:12px; background:#fafafa;">
  <div style="display:flex; gap:10px; flex-wrap:wrap;">
    <span style="font-size:12px; padding:6px 10px; border-radius:999px; background:#111; color:#fff; font-weight:700;">
      Diseño original
    </span>
    <span style="font-size:12px; padding:6px 10px; border-radius:999px; background:#111; color:#fff; font-weight:700;">
      Calidad premium
    </span>
    <span style="font-size:12px; padding:6px 10px; border-radius:999px; background:#111; color:#fff; font-weight:700;">
      Listo para usar
    </span>
    <span style="font-size:12px; padding:6px 10px; border-radius:999px; background:#111; color:#fff; font-weight:700;">
      Hecho para durar
    </span>
  </div>

  <p style="margin:10px 0 0; font-size:13px; color:#444;">
    Materiales seleccionados y terminaciones cuidadas para un resultado cómodo, resistente y con excelente presencia visual.
  </p>
</div>

<!-- Detalles -->
<div style="margin-top:12px; padding:14px 16px; border:1px solid #e7e7e7; border-radius:12px; background:#fff;">
  <h4 style="margin:0 0 8px; font-size:14px; font-weight:800;">
    Detalles del producto
  </h4>

  <ul style="margin:0; padding-left:18px; font-size:13px; color:#333;">
    <li style="margin:6px 0;">Terminaciones prolijas y excelente calidad de impresión.</li>
    <li style="margin:6px 0;">Pensado para uso diario y decoración.</li>
    <li style="margin:6px 0;">Ideal para living, dormitorio, oficina o espacios creativos.</li>
    <li style="margin:6px 0;">Aporta color y personalidad sin recargar el ambiente.</li>
  </ul>
</div>

<!-- Cuidados -->
<div style="margin-top:12px; padding:14px 16px; border:1px solid #e7e7e7; border-radius:12px; background:#fff;">
  <h4 style="margin:0 0 8px; font-size:14px; font-weight:800;">
    Cuidados recomendados
  </h4>

  <ul style="margin:0; padding-left:18px; font-size:13px; color:#333;">
    <li style="margin:6px 0;">Limpiar con paño suave o lavado delicado (según material).</li>
    <li style="margin:6px 0;">Evitar cloro y productos abrasivos.</li>
    <li style="margin:6px 0;">Secar a la sombra para conservar colores.</li>
  </ul>
</div>`;
  };

  const handleApplyToProducts = async () => {
    if (!selectedCategory) { showError('Selecciona una categoría'); return; }
    if (!htmlContent.trim()) { showError('La descripción no puede estar vacía'); return; }

    const confirmed = await showConfirm(
      '¿Aplicar descripción?',
      `Esto actualizará la descripción de TODOS los productos en la categoría "${selectedCategory.name}". Esta acción no se puede deshacer.`
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/products/bulk-description`, {
        method: 'PUT',
        headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
        body: JSON.stringify({ categoryId: selectedCategory._id, description: htmlContent })
      });
      if (response.ok) {
        const result = await response.json();
        showSuccess(`Descripción aplicada a ${result.modifiedCount} productos`);
      } else {
        const error = await response.json();
        showError(error.error || 'Error al aplicar');
      }
    } catch (error) {
      showError('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="descmgr-panel">
      {/* Header */}
      <div className="descmgr-header">
        <div>
          <h1>Gestor de Descripciones</h1>
          <p>Edita y aplica descripciones HTML masivamente por categoría</p>
        </div>
      </div>

      <div className="descmgr-layout">
        {/* Sidebar - Categories */}
        <div className="descmgr-sidebar">
          <div className="descmgr-sidebar-header">
            <i className="fas fa-list"></i>
            <span>Categorías</span>
          </div>
          <div className="descmgr-cat-list">
            {categories.map((category) => (
              <button
                key={category._id}
                className={`descmgr-cat-item ${selectedCategory?._id === category._id ? 'active' : ''}`}
                onClick={() => handleSelectCategory(category)}
              >
                <i className="fas fa-folder"></i>
                <span>{category.name}</span>
                {selectedCategory?._id === category._id && <i className="fas fa-chevron-right descmgr-cat-arrow"></i>}
              </button>
            ))}
          </div>
        </div>

        {/* Main - Editor */}
        <div className="descmgr-main">
          {selectedCategory ? (
            <div className="descmgr-editor-card">
              <div className="descmgr-editor-header">
                <div className="descmgr-editor-title">
                  <i className="fas fa-edit"></i>
                  <h3>{selectedCategory.name}</h3>
                </div>
                <div className="descmgr-mode-toggle">
                  <button className={`descmgr-mode-btn ${!previewMode ? 'active' : ''}`} onClick={() => setPreviewMode(false)}>
                    <i className="fas fa-code"></i> Código
                  </button>
                  <button className={`descmgr-mode-btn ${previewMode ? 'active' : ''}`} onClick={() => setPreviewMode(true)}>
                    <i className="fas fa-eye"></i> Vista Previa
                  </button>
                </div>
              </div>

              <div className="descmgr-editor-body">
                {previewMode ? (
                  <div className="descmgr-preview" dangerouslySetInnerHTML={{ __html: htmlContent }} />
                ) : (
                  <textarea
                    className="descmgr-textarea"
                    rows="20"
                    value={htmlContent}
                    onChange={(e) => setHtmlContent(e.target.value)}
                    placeholder="Escribe el HTML de la descripción..."
                  />
                )}
              </div>

              <div className="descmgr-editor-footer">
                <button className="descmgr-apply-btn" onClick={handleApplyToProducts} disabled={saving}>
                  {saving ? (
                    <><i className="fas fa-spinner fa-spin"></i> Aplicando...</>
                  ) : (
                    <><i className="fas fa-sync"></i> Aplicar a Todos los Productos</>
                  )}
                </button>
                <button className="descmgr-close-btn"
                  onClick={() => { setSelectedCategory(null); setHtmlContent(''); }}>
                  <i className="fas fa-times"></i> Cerrar
                </button>
              </div>
            </div>
          ) : (
            <div className="descmgr-empty">
              <i className="fas fa-hand-pointer"></i>
              <h3>Selecciona una categoría</h3>
              <p>Elige una categoría del panel izquierdo para editar la descripción de sus productos.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DescriptionManager;
